import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAuthStore } from "./store/authStore";
import { Toaster } from "./components/ui/toaster";

// Layout Components
import Layout from "./components/layout/Layout";
import AuthLayout from "./components/layout/AuthLayout";

// Pages
import LoginPage from "./pages/auth/LoginPage";
import ForcePasswordChangePage from "./pages/auth/ForcePasswordChangePage";
import DashboardPage from "./pages/DashboardPage";
import CasesPage from "./pages/cases/CasesPage";
import CaseDetailPage from "./pages/cases/CaseDetailPage";
import NewCasePage from "./pages/cases/NewCasePage";
import PlannedCasesPage from "./pages/cases/PlannedCasesPage";
import MobileCasesPage from "./pages/mobile-cases/MobileCasesPage";
import OrganizationsPage from "./pages/organizations/OrganizationsPage";
import NewOrganizationPage from "./pages/organizations/NewOrganizationPage";
import EditOrganizationPage from "./pages/organizations/EditOrganizationPage";
import OrganizationDetailPage from "./pages/organizations/OrganizationDetailPage";
import UsersPage from "./pages/users/UsersPage";
import BulkMobileUserPage from "./pages/users/BulkMobileUserPage";
import PendingApprovalPage from "./pages/users/PendingApprovalPage";
import TeamsPage from "./pages/teams/TeamsPage";
import PersonnelPage from "./pages/personnel/PersonnelPage";
import AttendanceKioskPage from "./pages/attendance/AttendanceKioskPage";
import AttendanceMonitorPage from "./pages/attendance/AttendanceMonitorPage";
import CheckinPage from "./pages/attendance/CheckinPage";
import PuantajPage from "./pages/personnel/PuantajPage";
import PuantajimPage from "./pages/personnel/PuantajimPage";
import DailyTeamAssignmentPage from "./pages/teams/DailyTeamAssignmentPage";
import TeamStatusPage from "./pages/teams/TeamStatusPage";
import HospitalsPage from "./pages/hospitals/HospitalsPage";
import PricingOrganizationsPage from "./pages/pricing/PricingOrganizationsPage";
import ZoneManagementPage from "./pages/pricing/ZoneManagementPage";
import SettingsPage from "./pages/SettingsPage";
import ProfilePage from "./pages/ProfilePage";
import AccessDeniedPage from "./pages/AccessDeniedPage";
import DeleteAccountPage from "./pages/mobile/DeleteAccountPage";
import CaseReportPage from "./pages/reports/CaseReportPage";
import MedicalDefinitionsPage from "./pages/medical/MedicalDefinitionsPage";
import LookupsPage from "./pages/lookups/LookupsPage";
import QuotesPage from "./pages/quotes/QuotesPage";
import NewQuotePage from "./pages/quotes/NewQuotePage";
import QuoteDetailPage from "./pages/quotes/QuoteDetailPage";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-medical-50 to-blue-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Zorunlu ilk-giriş şifre değişimi: bayrak set ise tüm uygulamayı kilitle.
  if (user?.must_change_password) {
    return <Navigate to="/sifre-degistir" replace />;
  }

  return children;
};

// Role-based Protected Route Component
const RoleProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-medical-50 to-blue-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has required role
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <AccessDeniedPage />;
  }

  return children;
};

// Forced password change route guard (outside main Layout)
const ForcePasswordChangeRoute = () => {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-medical-50 to-blue-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Bayrak yoksa bu ekrana erişime gerek yok.
  if (!user?.must_change_password) {
    return <Navigate to="/dashboard" replace />;
  }

  return <ForcePasswordChangePage />;
};

// Public Route Component (redirects to dashboard if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-medical-50 to-blue-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  const { initialize, isLoading, setupAuthListener } = useAuthStore();

  React.useEffect(() => {
    initialize();
    // Set up auth state change listener and get cleanup function
    const unsubscribe = setupAuthListener();

    // Safety timeout - force loading to false after 10 seconds
    const safetyTimeout = setTimeout(() => {
      const currentState = useAuthStore.getState();
      if (currentState.isLoading) {
        console.warn("⚠️ Loading timeout - forcing loading to false");
        useAuthStore.setState({ isLoading: false });
      }
    }, 10000);

    // Cleanup listener on unmount
    return () => {
      clearTimeout(safetyTimeout);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [initialize, setupAuthListener]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-medical-50 to-blue-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Sistem başlatılıyor...</p>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="min-h-screen bg-background">
          <Routes>
            {/* Public Routes */}
            <Route
              path="/v1/mobil/delete-account"
              element={<DeleteAccountPage />}
            />
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <AuthLayout>
                    <LoginPage />
                  </AuthLayout>
                </PublicRoute>
              }
            />

            {/* Zorunlu ilk-giriş şifre değişimi (Layout dışı, kendi guard'ı) */}
            <Route path="/sifre-degistir" element={<ForcePasswordChangeRoute />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />

              {/* Cases Routes - All roles can access, but external_institution/external_hospital has restrictions */}
              <Route path="cases" element={<CasesPage />} />
              <Route 
                path="cases/new" 
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "team_leader", "operator", "external_presidency"]}>
                    <NewCasePage />
                  </RoleProtectedRoute>
                } 
              />
              <Route
                path="cases/planned" 
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "team_leader", "operator", "paramedic", "external_presidency", "external_hospital", "external_parent_institution"]}>
                    <PlannedCasesPage />
                  </RoleProtectedRoute>
                } 
              />
              <Route path="cases/:id" element={<CaseDetailPage />} />
              
              {/* Mobile Cases Routes - All roles except external_institution */}
              <Route 
                path="mobile-cases" 
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "team_leader", "operator"]}>
                    <MobileCasesPage />
                  </RoleProtectedRoute>
                } 
              />

              {/* Organizations Routes - Super Admin / Admin ve diğer roller */}
              <Route 
                path="organizations" 
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "team_leader", "operator", "paramedic"]}>
                    <OrganizationsPage />
                  </RoleProtectedRoute>
                } 
              />
              <Route
                path="organizations/new"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "team_leader", "operator"]}>
                    <NewOrganizationPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="organizations/:id/edit"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "team_leader", "operator"]}>
                    <EditOrganizationPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="organizations/:id"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "team_leader", "operator", "paramedic"]}>
                    <OrganizationDetailPage />
                  </RoleProtectedRoute>
                }
              />

              {/* Pricing Routes (Kurumlar) - Admin, Super Admin, Supervisor, Team Leader, External Institution, External Parent Institution */}
              <Route
                path="pricing"
                element={
                  <RoleProtectedRoute
                    allowedRoles={["super_admin", "admin", "supervisor", "team_leader", "external_institution", "external_parent_institution"]}
                  >
                    <PricingOrganizationsPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="pricing/zones"
                element={
                  <RoleProtectedRoute
                    allowedRoles={["super_admin", "admin", "supervisor", "team_leader"]}
                  >
                    <ZoneManagementPage />
                  </RoleProtectedRoute>
                }
              />

              {/* Management Routes - Admin/Super Admin and External Institution */}
              <Route
                path="users"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "external_institution"]}>
                    <UsersPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="users/bulk-mobile"
                element={
                  <RoleProtectedRoute allowedRoles={["external_institution"]}>
                    <BulkMobileUserPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="users/pending-approval"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin"]}>
                    <PendingApprovalPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="teams"
                element={
                  <RoleProtectedRoute
                    allowedRoles={["super_admin", "admin", "supervisor", "team_leader"]}
                  >
                    <TeamsPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="personnel"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "ik"]}>
                    <PersonnelPage />
                  </RoleProtectedRoute>
                }
              />
              {/* Puantaj: yönetim ızgarası + personel kendi görünümü */}
              <Route
                path="personnel/puantaj"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "team_leader", "operator", "ik"]}>
                    <PuantajPage />
                  </RoleProtectedRoute>
                }
              />
              <Route path="puantajim" element={<PuantajimPage />} />

              {/* Yoklama: giriş-çıkış (tüm personel), kiosk + izleme (yetkili) */}
              <Route path="checkin" element={<CheckinPage />} />
              <Route
                path="attendance/kiosk"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "team_leader", "operator", "ik"]}>
                    <AttendanceKioskPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="attendance"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "team_leader", "operator", "ik"]}>
                    <AttendanceMonitorPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="teams/daily"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "team_leader", "operator"]}>
                    <DailyTeamAssignmentPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="teams/status"
                element={
                  <RoleProtectedRoute
                    allowedRoles={["super_admin", "admin", "supervisor", "team_leader", "operator"]}
                  >
                    <TeamStatusPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="hospitals"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "external_presidency", "external_hospital"]}>
                    <HospitalsPage />
                  </RoleProtectedRoute>
                }
              />

              {/* User Routes - All roles can access */}
              <Route path="profile" element={<ProfilePage />} />
              <Route path="settings" element={<SettingsPage />} />
              
              {/* Medical Definitions Route - Admin / Super Admin / Supervisor / Team Leader */}
              <Route
                path="medical-definitions"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "team_leader"]}>
                    <MedicalDefinitionsPage />
                  </RoleProtectedRoute>
                }
              />

              {/* Lookup Definitions Route - Super Admin / Admin / Supervisor */}
              <Route
                path="lookup-definitions"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor"]}>
                    <LookupsPage />
                  </RoleProtectedRoute>
                }
              />

              {/* Quotes Routes */}
              <Route
                path="quotes"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor"]}>
                    <QuotesPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="quotes/new"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor"]}>
                    <NewQuotePage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="quotes/:id"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor"]}>
                    <QuoteDetailPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="quotes/:id/edit"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor"]}>
                    <NewQuotePage />
                  </RoleProtectedRoute>
                }
              />

              {/* Reports Route - Admin / Super Admin / External Presidency / External Hospital / External Parent Institution */}
              <Route
                path="reports"
                element={
                  <RoleProtectedRoute allowedRoles={["super_admin", "admin", "supervisor", "external_presidency", "external_hospital", "external_parent_institution"]}>
                    <CaseReportPage />
                  </RoleProtectedRoute>
                }
              />
            </Route>

            {/* Access Denied Route */}
            <Route path="access-denied" element={<AccessDeniedPage />} />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>

          {/* Global Toast Notifications */}
          <Toaster />
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
